import React from 'react';
import { Field, FormikProvider, useFormik } from 'formik';
import { filedLawsuitInitialData } from 'app/edozvola/cases/filed-lawsuit/model/filed-lawsuit.model';
import { fetchFiledLawsuitQuery, saveFiledLawsuitQuery } from 'app/edozvola/cases/filed-lawsuit/service/filed-lawsuit.queries';
import { useParams } from 'react-router-dom';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { Col, Row } from 'reactstrap';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { Button } from 'edozvola-frontend-lib/dist/ui/components';
import { useDispatch } from 'react-redux';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { fetchLawsuitSolutionOptionsQuery } from 'app/edozvola/codebooks/codebooks.queries';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { filedLawsuitValidationSchema } from 'app/edozvola/cases/filed-lawsuit/model/filed-lawsuit.schema';
import {PaginatedDropdown} from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";
import { getCaseContext } from 'app/shared/util/context-util';
import { ToggleWithLabels } from 'edozvola-frontend-lib/dist/ui/components/toggle-with-labels';

const FiledLawsuit = () => {
  const { id: uuid } = useParams<{ id: string }>();
  const { sidebarItemPair } = getCaseContext();
  const dispatch = useDispatch();

  const { data: lawsuitSolutionOptions, isLoading: lawsuitSolutionOptionsLoading } = fetchLawsuitSolutionOptionsQuery();
  const { data: filedLawsuitData, isLoading: filedLawsuitLoading } = fetchFiledLawsuitQuery(uuid);

  const formik = useFormik({
    initialValues: filedLawsuitData || filedLawsuitInitialData,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: filedLawsuitValidationSchema,
    onSubmit: values => saveFiledLawsuit(values),
  });

  const { mutate: saveFiledLawsuit, isPending: savingFiledLawsuit } = saveFiledLawsuitQuery(uuid, formik.resetForm);

  return (
    <>
      <FormikProvider value={formik}>
        <FormikValueObserver
          onChange={dirty => {
            if (dirty.dirty) {
              dispatch(setFormChanged(true));
            } else {
              dispatch(initCaseFormChange());
            }
          }}
        />
        <div className="form-header">Tužba</div>
        <div className={'d-flex justify-content-between flex-column gap-3'}>
          <Row>
            <Col md={6}>
              <Label labelText={'Pokrenuta tužba'} />
              <ToggleWithLabels
                trueLabel="DA"
                falseLabel="NE"
                disabled={sidebarItemPair?.readOnly}
                checked={formik.values.lawsuitFiled}
                id="iban"
                name="iban"
                onChange={event => formik.setFieldValue('lawsuitFiled', event.target.checked)}
                onBlur={formik.handleBlur}
              />
            </Col>
            <Col md={6}>
              <Label labelText={'Datum pokrenute tužbe'} />
              <Field
                id="dateOfFilingLawsuit"
                name="dateOfFilingLawsuit"
                render={({ field }) => (
                  <FormikDatepicker
                    disabled={sidebarItemPair?.readOnly}
                    field={field}
                    setFieldValue={formik.setFieldValue}
                    dateFormat={APP_DATE_FORMAT}
                  />
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Label labelText={'Odluka suda'} />
              <PaginatedDropdown
                placeholder={!sidebarItemPair?.readOnly && 'Odaberi odluku suda'}
                onItemSelect={selected => formik.setFieldValue('lawsuitSolution', selected)}
                selected={formik.values.lawsuitSolution}
                labelKey={'value'}
                options={lawsuitSolutionOptionsLoading ? [] : lawsuitSolutionOptions}
                disabled={sidebarItemPair?.readOnly}
                clearButton
                error={!sidebarItemPair?.readOnly && (formik.errors.lawsuitSolution as string)}
              />
            </Col>
            <Col md={6}>
              <Label labelText={'Datum odluke'} />
              <Field
                id="lawsuitSolutionDate"
                name="lawsuitSolutionDate"
                render={({ field }) => (
                  <FormikDatepicker
                    disabled={sidebarItemPair?.readOnly}
                    field={field}
                    setFieldValue={formik.setFieldValue}
                    dateFormat={APP_DATE_FORMAT}
                  />
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Label labelText={'Odluka drugog stupnja'} />
              <PaginatedDropdown
                placeholder={!sidebarItemPair?.readOnly && 'Odaberi odluku drugog stupnja'}
                onItemSelect={selected => formik.setFieldValue('lawsuitSolutionSecondDegree', selected)}
                selected={formik.values.lawsuitSolutionSecondDegree}
                labelKey={'value'}
                options={lawsuitSolutionOptionsLoading ? [] : lawsuitSolutionOptions}
                disabled={sidebarItemPair?.readOnly}
                error={!sidebarItemPair?.readOnly && ((formik.errors.lawsuitSolutionSecondDegree as string) ?? '')}
                clearButton={!formik.values.lawsuitFiled}
              />
            </Col>
            <Col md={6}>
              <Label labelText={'Datum odluke'} />
              <Field
                id="lawsuitSolutionSecondDegreeDate"
                name="lawsuitSolutionSecondDegreeDate"
                render={({ field }) => (
                  <FormikDatepicker
                    disabled={sidebarItemPair?.readOnly}
                    field={field}
                    setFieldValue={formik.setFieldValue}
                    dateFormat={APP_DATE_FORMAT}
                  />
                )}
              />
            </Col>
          </Row>
        </div>
        {!sidebarItemPair?.readOnly && (
          <div className={'case-save-button'}>
            <Button
              label={'Spremi'}
              size={'lg'}
              disabled={!formik.dirty || !formik.isValid}
              isLoading={savingFiledLawsuit}
              color={'info'}
              onClick={() => formik.handleSubmit()}
            />
          </div>
        )}
      </FormikProvider>
    </>
  );
};

export default FiledLawsuit
